import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonPrimary,
  ButtonSize,
  IconArrowRight,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import { ChannelStatus, ChannelType } from '../hoc/types';
import { useAssignEmployeeToChatMutation } from '../hoc/store/services';
import ChatMessages from './chat-messages';
import { CompletedMessageTypes } from './types';

import cn from './messages.module.css';

const CompletedMessage = ({
  path,
  messages,
  status,
  setAssignPanelIsOpen,
  getEmployees,
  messageId,
  scrollWrapperRef,
  getNextMessages,
  pagination,
  messagesIsFetching,
  scrollRef,
  setCurrentScrollTop,
  prevMessageIsFetching,
  onReopen,
  changeChatStatusInProgress,
  channelType,
}: CompletedMessageTypes) => {
  const navigate = useNavigate();

  const {
    messagesDictionaries: { activePermissions },
    messages: {
      general: { employee },
    },
  } = useAppSelector((state) => state);

  const [assignToChat, { isLoading, isSuccess }] =
    useAssignEmployeeToChatMutation();

  const assignToChatAction = () => {
    assignToChat({
      id: messageId as string,
      body: {
        employeeId: employee?.id as string,
      },
    });
  };

  useEffect(() => {
    if (isSuccess) {
      navigate(path);
    }
  }, [isSuccess]);

  return (
    <div
      className={`${cn.completedMessageContent} ${
        status !== ChannelStatus.CLOSED
          ? cn.completedMessageContentSmallHeight
          : ''
      }`}
    >
      {status === ChannelStatus.CLOSED && (
        <div className={cn.completedMessageTop}>
          <div className={cn.completedMessageTopText}>Chat closed</div>
          {activePermissions?.includes(
            PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ME_ACTION,
          ) &&
            channelType === ChannelType.TRANSACTION && (
              <ButtonPrimary
                text={changeChatStatusInProgress ? 'Loading...' : 'Reopen'}
                className={cn.completedMessageReopenButton}
                size={ButtonSize.SMALL}
                onClick={onReopen}
                bold
              />
            )}
        </div>
      )}

      <div className={cn.completedMessageChat} ref={scrollWrapperRef}>
        <ChatMessages
          messages={messages}
          scrollWrapperRef={scrollWrapperRef}
          messagesIsFetching={messagesIsFetching}
          scrollRef={scrollRef}
          getNextMessages={getNextMessages}
          pagination={pagination}
          setCurrentScrollTop={setCurrentScrollTop}
          prevMessageIsFetching={prevMessageIsFetching}
          messageId={messageId}
        />
        {!messages?.length && (
          <div className={cn.emptyMessages}>There are no messages</div>
        )}
      </div>
      {status !== ChannelStatus.CLOSED && (
        <div className={cn.completedChatControls}>
          {activePermissions?.includes(
            PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ME_ACTION,
          ) && (
            <button
              type="button"
              className={`${cn.startChatButton} ${cn.withMargin}`}
              onClick={() => {
                assignToChatAction();
              }}
            >
              {isLoading ? 'Loading...' : 'Start Chat'}
            </button>
          )}
          {activePermissions?.includes(
            PermissionEnum.MESSAGES_ASSIGN_CHAT_TO_ANOTHER_ACTION,
          ) && (
            <button
              type="button"
              className={cn.assignUserButton}
              onClick={() => {
                setAssignPanelIsOpen(true);
                getEmployees();
              }}
            >
              Assign
              <IconArrowRight />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CompletedMessage;
